import React from 'react';
import { Box, Tooltip, IconButton, Chip, Typography } from '@mui/material';
import { CallMerge } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  activateMergeTool,
  deactivateMergeTool,
  selectIsMergeToolActive,
} from '../store/slices/mergeToolSlice';
import { selectSelectedFeatures } from '../store/slices/selectionSlice';

/**
 * MergeToolButton
 *
 * A toolbar button that activates the polygon merge tool. When activated,
 * the selected polygon features can be merged into a single feature.
 *
 * **Validates: Requirement 15.1** – WHEN a Registered_User selects two or more
 * adjacent polygon features and activates merge tool, THE Vector_Editor SHALL
 * combine the polygons into a single feature.
 *
 * Behaviour:
 * - Disabled when fewer than 2 polygon features are selected
 * - Clicking toggles the merge tool on/off
 * - Shows a status chip when the merge tool is active
 * - Only enabled for polygon layers
 */
const MergeToolButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const isMergeToolActive = useAppSelector(selectIsMergeToolActive);
  const selectedFeatures = useAppSelector(selectSelectedFeatures);
  const layers = useAppSelector((state) => state.layers.layers);

  // Determine if we have 2+ polygon features selected
  const selectedPolygonCount = selectedFeatures.filter((sf) => {
    const layer = layers.find((l) => l.id === sf.layerId);
    return layer?.geometryType === 'Polygon';
  }).length;

  const hasEnoughPolygons = selectedPolygonCount >= 2;
  const canActivate = hasEnoughPolygons && !isMergeToolActive;
  const canDeactivate = isMergeToolActive;
  const isDisabled = !canActivate && !canDeactivate;

  const handleToggle = () => {
    if (isMergeToolActive) {
      dispatch(deactivateMergeTool());
    } else if (hasEnoughPolygons) {
      dispatch(activateMergeTool());
    }
  };

  const getTooltipText = (): string => {
    if (isMergeToolActive) {
      return 'Deactivate Merge Tool';
    }
    if (!hasEnoughPolygons) {
      return 'Select 2 or more polygon features to merge';
    }
    return 'Merge Polygons';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={getTooltipText()} placement="bottom" arrow>
        <span>
          <IconButton
            size="small"
            onClick={handleToggle}
            disabled={isDisabled}
            aria-label={isMergeToolActive ? 'Deactivate merge tool' : 'Activate merge tool'}
            aria-pressed={isMergeToolActive}
            data-testid="merge-tool-button"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '6px',
              color: isMergeToolActive ? '#4fc3f7' : '#c8c8c8',
              backgroundColor: isMergeToolActive ? 'rgba(79,195,247,0.15)' : 'transparent',
              border: isMergeToolActive
                ? '1px solid rgba(79,195,247,0.4)'
                : '1px solid transparent',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: '#fff',
              },
              '&.Mui-disabled': {
                color: '#555',
              },
            }}
          >
            <CallMerge sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>

      {/* Status indicator when merge tool is active */}
      {isMergeToolActive && (
        <Chip
          label={`Merge: ${selectedPolygonCount} polygons selected`}
          size="small"
          onDelete={handleToggle}
          sx={{
            backgroundColor: 'rgba(79,195,247,0.12)',
            color: '#4fc3f7',
            fontSize: '0.65rem',
            height: 20,
            maxWidth: 220,
            border: '1px solid rgba(79,195,247,0.3)',
            '& .MuiChip-deleteIcon': {
              color: '#4fc3f7',
              fontSize: 14,
              '&:hover': {
                color: '#fff',
              },
            },
          }}
        />
      )}

      {/* Hint when not enough features are selected */}
      {!isMergeToolActive && selectedFeatures.length > 0 && !hasEnoughPolygons && (
        <Typography sx={{ color: '#555', fontSize: '0.65rem' }}>
          Select 2+ polygons to merge
        </Typography>
      )}
    </Box>
  );
};

export default MergeToolButton;
